import styled, { css } from 'styled-components';
import { breakpoint } from 'styles/mixins';
import { rem } from '@helpers/style';

import { StyledLightBox } from 'styles/common';

const animation = css`
	animation-duration: 250ms;
	animation-fill-mode: forwards;
	transition-timing-function: ${(p) => p.theme.transition.function};
`;

export const StyledDialog = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	z-index: 2140;
	color: ${(p) => p.theme.color.textColor};
	font-size: ${(p) => rem(14, p.theme.fontSize.base)};

	@keyframes anim-open-top {
		0% {
			opacity: 0;
			transform: scale3d(1.1, 1.1, 1);
		}
		100% {
			opacity: 1;
			transform: scale3d(1, 1, 1);
		}
	}
	@keyframes anim-close-top {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
			transform: scale3d(0.9, 0.9, 1);
		}
	}

	@keyframes anim-mobile-open {
		0% {
			opacity: 1;
			transform: translateY(100%);
		}
		100% {
			transform: translateY(0);
		}
	}
	@keyframes anim-mobile-close {
		100% {
			transform: translateY(100%);
		}
	}

	@keyframes anim-open-center {
		0% {
			opacity: 0;
			transform: scale3d(1.1, 1.1, 1) translateY(-50%);
		}
		100% {
			opacity: 1;
			transform: scale3d(1, 1, 1) translateY(-50%);
		}
	}
	@keyframes anim-close-center {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
			transform: scale3d(0.9, 0.9, 1) translateY(-50%);
		}
	}

	@keyframes lightbox-open {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}
	@keyframes lightbox-close {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}
`;

export const Container = styled.div`
	position: relative;
	z-index: 1142;
	height: 100%;
	width: 100%;
	margin: 0 auto;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
`;

export const LightBox = styled(StyledLightBox)`
  ${breakpoint.lessThan('tablet')`
    ${(p) =>
			p.$type === 'modal'
				? css`
						display: none;
				  `
				: css`
						animation-name: ${p.$close ? 'lightbox-close' : 'lightbox-open'};
				  `}
  `}

  ${animation}

  ${breakpoint.greaterThan('tablet')`
    animation-name: ${(p) => (p.$close ? 'lightbox-close' : 'lightbox-open')};
  `}
`;

const size = {
	lg: css`
		width: 750px;
	`,
	md: css`
		width: 550px;
	`,
	sm: css`
		width: 400px;
	`,
	xs: css`
		width: 320px;
	`,
};

export const Box = styled.div.attrs((p) => ({ $size: p.$size || 'sm' }))`
  width: ${(p) => (p.$type === 'modal' ? '100%' : '90%')};
  min-width: 320px;
  margin: 0 auto;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  background: ${(p) => p.theme.color.fgColor};
  position: relative;
  z-index: 3000;
  overflow: hidden;
  border-radius: 10px;

  ${(p) =>
		!p.$animateEnded &&
		p.$type === 'modal' &&
		css`
			animation-name: ${p.$close ? `anim-mobile-close` : `anim-mobile-open`};
		`}

  ${(p) =>
		!p.$animateEnded &&
		p.$type !== 'modal' &&
		css`
			animation-name: ${p.$close ? `anim-close-center` : `anim-open-center`};
		`}

  -webkit-overflow-scrolling: touch;

  ${breakpoint.lessThan('tablet')`
    ${(p) =>
			p.$type === 'modal'
				? css`
						overflow-y: auto;
						position: fixed;
						top: 0;
				  `
				: css`
						top: 50%;
						transform: translateY(-50%);
						position: relative;
				  `}

  `}

  ${animation}

  ${breakpoint.greaterThan('tablet')`
    ${(p) =>
			p.$align !== 'top' &&
			css`
				transform: translateY(-50%);
			`}
    animation-name: ${(p) =>
			p.$close ? `anim-close-${p.$align}` : `anim-open-${p.$align}`};
  `}

  p {
    margin: 0;
  }

  ${(p) =>
		p.$type === 'modal'
			? css`
					height: 100%;
			  `
			: css`
					text-align: center;
			  `}

  ${breakpoint.greaterThan('tablet')`
    top: 50%;
    width: 750px;
    height: auto;
    max-height: inherit !important;

    ${(p) =>
			p.$type === 'modal' &&
			css`
				min-height: ${p.$height || 200}px;
			`}

    ${(p) => size[p.$size]}

    ${(p) =>
			p.$align === 'top' &&
			css`
				height: auto;
				top: 5%;
				margin-bottom: 20px;
				transform: translateY(0);
			`}
  `}
`;

export const Header = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	overflow: hidden;
	height: 60px;
	line-height: 60px;
	padding: 0 10px;
	direction: rtl;
	z-index: 1000;
	color: ${(p) => p.theme.color.textColor};
	background-color: ${(p) => p.theme.color.fgColor};
	/* border-bottom: 1px solid ${(p) => p.theme.color.border}; */

	${breakpoint.greaterThan('tablet')`
    position: absolute;
    height: 60px;
    line-height: 60px;
    padding: 0 15px;
    border-radius: 10px 10px 0 0;
  `}

	h3 {
		font-size: ${(p) => rem(18, p.theme.fontSize.base)};
		margin: 0;
		text-align: center;
		display: inline-block;
		white-space: nowrap;
		width: 100%;
		position: relative;
		z-index: 1;
	}
`;

const Button = styled.div`
	position: absolute;
	top: 50%;
	width: 40px;
	height: 40px;
	line-height: 40px;
	text-align: center;
	z-index: 2;
	cursor: pointer;
	transform: translateY(-50%);
	color: ${(p) => p.theme.color.textColor};

	i {
		font-size: 0.875rem;
	}
`;

export const CloseButton = styled(Button)`
	left: 10px;
`;

export const BackButton = styled(Button)`
	right: 10px;
`;

export const Body = styled.section`
	${(p) =>
		p.$type === 'modal'
			? css`
					padding-top: 60px;
			  `
			: css`
					padding: 20px;
			  `}
`;

export const IconContainer = styled.div`
	margin: 10px 0;

	.icon {
		font-size: ${(p) => rem(50, p.theme.fontSize.base)};
		color: ${(p) => p.theme.color.primary};
	}
`;

export const Footer = styled.footer`
	margin-top: 20px;
	direction: rtl;
	min-height: 34px;
`;
